import { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail, Clock, CheckCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPassword() {
    const [email, setEmail] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        try {
            await axios.post(`${API}/auth/forgot-password`, { email });
            setIsSubmitted(true);
            toast.success('Password reset instructions sent!');
        } catch (error) {
            // Show success even if email doesn't exist (security best practice)
            setIsSubmitted(true);
            toast.success('If an account exists, reset instructions have been sent.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50 flex flex-col">
            {/* Hero Section */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 py-12">
                {/* Logo */}
                <div className="flex flex-col items-center mb-6">
                    <img 
                        src="/voilaw-logo.png" 
                        alt="Voilaw Logo" 
                        className="h-32 w-32 object-contain"
                    />
                    <h1 className="font-brand text-3xl text-foreground mt-4">Voilaw</h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Time & task tracking for attorneys
                    </p>
                </div>

                {isSubmitted ? (
                    // Success state
                    <div className="w-full max-w-sm text-center">
                        <div className="p-4 bg-emerald-100 rounded-full w-20 h-20 mx-auto mb-6 flex items-center justify-center">
                            <CheckCircle className="h-10 w-10 text-emerald-600" />
                        </div>
                        <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                            Check your email
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            If an account exists for <strong>{email}</strong>, we&apos;ve sent password reset instructions.
                        </p>
                        <Link
                            to="/login"
                            className="inline-flex items-center gap-2 text-[#1C6EE7] font-semibold hover:underline"
                        >
                            <ArrowLeft className="h-4 w-4" />
                            Back to sign in
                        </Link>
                    </div>
                ) : (
                    // Form state
                    <>
                        <div className="text-center mb-10 max-w-sm">
                            <div className="p-4 bg-blue-100 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                                <Mail className="h-8 w-8 text-[#1C6EE7]" />
                            </div>
                            <h2 className="font-heading text-2xl font-semibold text-foreground mb-2">
                                Forgot your password?
                            </h2>
                            <p className="text-muted-foreground">
                                Enter your email and we&apos;ll send you instructions to reset your password.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="w-full max-w-sm space-y-4">
                            <div className="space-y-2">
                                <Label htmlFor="email" className="text-foreground">Email</Label>
                                <Input
                                    id="email"
                                    type="email"
                                    placeholder="attorney@lawfirm.com"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    data-testid="forgot-password-email-input"
                                    className="h-14 rounded-2xl bg-white shadow-soft border-0 text-lg px-5"
                                />
                            </div>
                            
                            <div className="pt-4">
                                <Button
                                    type="submit"
                                    className="w-full sm:w-3/4 mx-auto block h-14 rounded-full text-lg font-semibold bg-[#1C6EE7] hover:bg-[#1559c7] shadow-lg hover:shadow-xl transition-all"
                                    disabled={isLoading}
                                    data-testid="forgot-password-submit-btn"
                                >
                                    {isLoading ? (
                                        <span className="flex items-center justify-center gap-2">
                                            <Clock className="h-5 w-5 animate-spin" />
                                            Sending...
                                        </span>
                                    ) : (
                                        'Send reset instructions'
                                    )}
                                </Button>
                            </div>
                        </form>

                        <div className="mt-8 text-center">
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground"
                                data-testid="back-to-login-link"
                            >
                                <ArrowLeft className="h-4 w-4" />
                                Back to sign in
                            </Link>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
